import { localVoiceOwnership } from './localVoiceOwnership'

const PROTOCOL = 'gse-native-team-voice-v1'
const uuidOK = value =>
  typeof value === 'string' &&
  /^[a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/i.test(value)
const roomOK = value => typeof value === 'string' && /^T[a-f0-9]{31}$/.test(value)
const text = (value, max = 180) => (typeof value === 'string' && value.length <= max ? value : '')
const date = value => (typeof value === 'string' ? Date.parse(value) : NaN)
// Each message names only a control the panel renders in that state (bug szpd7s32gyUGITfEhX7s):
// `uncertain` shows "Check this same call" and "Leave call"; an ended call shows Dismiss.
export const UNCERTAIN_ERROR =
  'This team call could not be confirmed. Check this same call or leave it.'
export const ENDED_ERROR =
  'The audio connection for this team call failed, so the call ended. Start the call again when you are ready.'
const ERROR_REASONS = new Set([
  'device-error',
  'connection-error',
  'connection-timeout',
  'status-unavailable',
  'credential-unavailable',
])
// The longest a session may sit in `closing` waiting for the SDK to confirm it stopped. Voice SDK 2.16:
// a Device destroyed before its signaling stream connects leaves device.connect() pending forever
// (PStream._destroy emits 'offline', never the 'close' that _setupStream's promise waits on), so the
// terminal proof can never arrive and the call used to stay on "Closing audio…" until the browser quit.
export const TEARDOWN_TIMEOUT_MS = 5000
// Twilio's error shape is { code, name, message }; none of it carries the credential.
const sdkError = error => ({ code: error?.code, name: error?.name, message: error?.message })

export function validateTeamGrant(value, session, now = Date.now()) {
  const input = session.input,
    scope = session.scope
  const conversationId =
    input.kind === 'group' ? input.groupId : [session.uid, input.peerId].sort().join('_')
  if (
    !value ||
    value.uid !== session.uid ||
    value.nativeProtocol !== PROTOCOL ||
    value.transport !== 'web' ||
    value.acceptanceId !== input.acceptanceId ||
    value.kind !== input.kind ||
    value.mode !== input.mode ||
    value.conversationId !== conversationId ||
    value.revision !== scope.revision ||
    !roomOK(value.roomId) ||
    (input.roomId && value.roomId !== input.roomId) ||
    !uuidOK(value.claimId) ||
    value.identity !== `gse_native_${value.claimId.toLowerCase().replaceAll('-', '')}` ||
    value.conferenceName !== `gse_team_${value.roomId}` ||
    !/^[a-f0-9]{64}$/.test(value.proof || '') ||
    typeof value.token !== 'string' ||
    value.token.length > 16000 ||
    !/^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(value.token) ||
    !(date(value.connectBefore) > now && date(value.connectBefore) <= now + 130000) ||
    !(date(value.expiresAt) >= date(value.connectBefore))
  )
    throw new Error('The team call credential is invalid or expired.')
  return value
}

// Credentials, nonces and SDK instances remain in this non-reactive client.
// Only the explicit public projection passed to onChange reaches UI state.
export class TeamVoiceClient {
  constructor({
    call,
    getDevice,
    identity,
    onChange = () => {},
    ownership = localVoiceOwnership,
    externalCall = () => false,
    uuid = () => crypto.randomUUID(),
    clock = Date.now,
    setTimer = setTimeout,
    clearTimer = clearTimeout,
  } = {}) {
    Object.assign(this, {
      call,
      getDevice,
      identity,
      onChange,
      ownership,
      externalCall,
      uuid,
      clock,
      setTimer,
      clearTimer,
    })
    this.session = null
  }

  current(session) {
    const who = this.identity()
    return (
      this.session === session &&
      !session.closing &&
      session.lease.isCurrent() &&
      who?.uid === session.uid &&
      who.allowed === true &&
      (session.input.mode !== 'ptt' || who.ptt === true) &&
      !this.externalCall()
    )
  }

  assertCurrent(session) {
    if (!this.current(session)) {
      if (this.session === session) this.end('access-changed')
      throw new Error('Team calling access or audio ownership changed.')
    }
  }

  reconcile() {
    if (this.session && !this.current(this.session)) this.end('access-changed')
  }

  publish(session, patch = {}) {
    if (this.session !== session) return
    Object.assign(session.view, patch)
    this.onChange({ ...session.view, people: session.view.people.map(person => ({ ...person })) })
  }

  async scope(target, mode) {
    const who = this.identity()
    if (
      !who?.uid ||
      who.allowed !== true ||
      !['open', 'ptt'].includes(mode) ||
      (mode === 'ptt' && who.ptt !== true)
    )
      throw new Error('Current staff team-calling access is required.')
    if (
      !['direct', 'group'].includes(target?.kind) ||
      (target.kind === 'direct' && (!text(target.peerId, 200) || target.peerId === who.uid)) ||
      (target.kind === 'group' && !text(target.groupId, 200))
    )
      throw new Error('Choose a current staff conversation.')
    const selected = {
      actorUid: who.uid,
      nativeProtocol: PROTOCOL,
      kind: target.kind,
      mode,
      ...(target.kind === 'direct' ? { peerId: target.peerId } : { groupId: target.groupId }),
    }
    const value = await this.call('getNativeStaffTeamVoiceScope', selected)
    const fresh = this.identity(),
      ids = value?.members?.map(person => person.uid)
    if (
      fresh?.uid !== who.uid ||
      fresh.allowed !== true ||
      (mode === 'ptt' && fresh.ptt !== true) ||
      value?.uid !== who.uid ||
      value.nativeProtocol !== PROTOCOL ||
      !/^[a-f0-9]{64}$/.test(value.revision || '') ||
      !Array.isArray(ids) ||
      ids.length < 2 ||
      ids.length > 50 ||
      new Set(ids).size !== ids.length ||
      !ids.includes(who.uid) ||
      ids.some(id => !text(id, 200)) ||
      (target.kind === 'direct' && (ids.length !== 2 || !ids.includes(target.peerId)))
    )
      throw new Error('Refresh this conversation before calling.')
    if (
      value.current &&
      (!roomOK(value.current.roomId) ||
        value.current.uid !== who.uid ||
        value.current.mode !== mode ||
        value.current.nativeProtocol !== PROTOCOL ||
        value.current.revision !== value.revision)
    )
      throw new Error('The current team room changed.')
    return {
      ...selected,
      revision: value.revision,
      label: text(value.label) || 'GSE team call',
      members: value.members.map(person => ({
        uid: person.uid,
        name: text(person.name) || 'Teammate',
      })),
      current: value.current || null,
    }
  }

  async start(scope) {
    if (this.session || this.externalCall())
      throw new Error('Finish the current audio session first.')
    const who = this.identity()
    if (
      !who?.uid ||
      scope.actorUid !== who.uid ||
      who.allowed !== true ||
      (scope.mode === 'ptt' && who.ptt !== true)
    )
      throw new Error('Current staff team-calling access is required.')
    const session = {
      uid: who.uid,
      scope,
      closing: false,
      grant: null,
      device: null,
      connection: null,
      connectPending: false,
      preparing: false,
      timers: new Set(),
      floor: null,
      input: {
        actorUid: who.uid,
        nativeProtocol: PROTOCOL,
        kind: scope.kind,
        mode: scope.mode,
        ...(scope.kind === 'direct' ? { peerId: scope.peerId } : { groupId: scope.groupId }),
        revision: scope.revision,
        operationId: this.uuid(),
        acceptanceId: this.uuid(),
        transport: 'web',
        ...(scope.current ? { roomId: scope.current.roomId } : {}),
      },
      view: {
        uid: who.uid,
        phase: 'preparing',
        label: scope.label,
        mode: scope.mode,
        roomId: scope.current?.roomId || null,
        people: scope.members.map(person => ({ ...person, phase: 'available' })),
        muted: false,
        transmitting: false,
        waitingForFloor: false,
        speaker: null,
        error: null,
      },
    }
    session.lease = this.ownership.reserve('team', () => this.end('company-call'))
    this.session = session
    this.publish(session)
    return this.prepare(session)
  }

  timer(session, callback, delay) {
    const id = this.setTimer(() => {
      session.timers.delete(id)
      callback()
    }, delay)
    session.timers.add(id)
    return id
  }

  async prepare(session = this.session) {
    if (!session || session.preparing || session.grant) return
    this.assertCurrent(session)
    session.preparing = true
    this.publish(session, { phase: 'preparing', error: null })
    try {
      const { roomId } = session.input
      const request = roomId
        ? {
            actorUid: session.uid,
            nativeProtocol: PROTOCOL,
            roomId,
            acceptanceId: session.input.acceptanceId,
            transport: 'web',
          }
        : session.input
      const value = await this.call('prepareNativeStaffTeamVoice', request)
      session.grant = validateTeamGrant(value, session, this.clock())
      if (!this.current(session)) {
        if (this.session === session) this.end('access-changed')
        await this.cleanup(session)
        return
      }
      this.publish(session, { phase: 'connecting', roomId: value.roomId })
      const Device = await this.getDevice()
      this.assertCurrent(session)
      session.device = new Device(value.token, {
        logLevel: 'error',
        codecPreferences: ['opus', 'pcmu'],
        allowIncomingWhileBusy: false,
        tokenRefreshMs: 30000,
      })
      session.device.on('tokenWillExpire', () => this.refresh(session))
      session.device.on('error', error => {
        if (this.session !== session) return
        console.warn('[TeamVoice] Twilio Device error', sdkError(error))
        this.end('device-error')
      })
      session.device.on('incoming', incoming => incoming.reject?.())
      const params = {
        To: `conference:${value.conferenceName}`,
        NativeTeamClaimId: value.claimId,
        NativeTeamRoomId: value.roomId,
        NativeTeamProof: value.proof,
      }
      this.assertCurrent(session)
      session.connectPending = true
      session.connectionTimer = this.timer(
        session,
        () => {
          if (this.current(session)) this.end('connection-timeout')
        },
        25000
      )
      let connection
      try {
        connection = await session.device.connect({ params })
      } finally {
        session.connectPending = false
      }
      session.connection = connection
      connection.mute(true)
      const terminal = () => this.terminal(session)
      for (const event of ['disconnect', 'cancel', 'reject']) connection.on(event, terminal)
      connection.on('error', error => {
        if (this.session !== session) return
        console.warn('[TeamVoice] Twilio call error', sdkError(error))
        this.end('connection-error')
      })
      connection.on('accept', () => this.accepted(session))
      connection.on('reconnecting', () => {
        if (!this.current(session)) return
        session.reconnecting = true
        this.release()
        connection.mute(true)
        this.publish(session, {
          phase: 'reconnecting',
          error: 'Connection interrupted. Your microphone is paused.',
        })
        if (!session.reconnectTimer)
          session.reconnectTimer = this.timer(
            session,
            () => {
              session.reconnectTimer = null
              if (this.session === session && session.reconnecting) this.end('connection-error')
            },
            20000
          )
      })
      connection.on('reconnected', () => {
        if (!this.current(session)) return
        session.reconnecting = false
        this.clearTimer(session.reconnectTimer)
        session.timers.delete(session.reconnectTimer)
        session.reconnectTimer = null
        this.accepted(session)
        this.publish(session, { phase: 'connecting', error: null })
        this.poll(session)
      })
      if (!this.current(session)) {
        this.stopMedia(session)
        return
      }
      if (connection.status?.() === 'closed') {
        terminal()
        return
      }
      if (connection.status?.() === 'open') this.accepted(session)
      this.poll(session)
    } catch (error) {
      if (session.device || session.connectPending || session.closing) {
        if (!session.closing) console.warn('[TeamVoice] Call setup failed', sdkError(error))
        // End first (it publishes the ended message), then stop media. stopMedia alone reached
        // terminal() silently, so a failed connect cleared the panel with no word to the caller.
        if (!session.closing) this.end('connection-error')
        else this.stopMedia(session)
      } else this.publish(session, { phase: 'uncertain', error: UNCERTAIN_ERROR })
    } finally {
      session.preparing = false
    }
  }

  accepted(session) {
    if (!this.current(session)) {
      if (this.session === session) this.end('access-changed')
      else this.stopMedia(session)
      return
    }
    if (session.reconnecting) return
    this.clearTimer(session.connectionTimer)
    session.timers.delete(session.connectionTimer)
    session.connectionTimer = null
    if (session.input.mode === 'open') session.connection?.mute(session.view.muted)
  }

  async poll(session) {
    if (!this.current(session)) {
      if (this.session === session) this.end('access-changed')
      return
    }
    if (session.polling) return
    this.clearTimer(session.pollTimer)
    session.timers.delete(session.pollTimer)
    session.pollTimer = null
    session.polling = true
    try {
      const value = await this.call('getNativeStaffTeamVoiceStatus', {
        actorUid: session.uid,
        nativeProtocol: PROTOCOL,
        roomId: session.grant.roomId,
      })
      if (!this.current(session)) {
        if (this.session === session) this.end('access-changed')
        return
      }
      if (
        value?.uid !== session.uid ||
        value.nativeProtocol !== PROTOCOL ||
        value.roomId !== session.grant.roomId ||
        value.revision !== session.scope.revision ||
        value.mode !== session.input.mode ||
        value.claimId !== session.grant.claimId ||
        value.transport !== 'web' ||
        !Array.isArray(value.people) ||
        value.people.length > 50 ||
        value.people.some(
          person => !session.scope.members.some(member => member.uid === person.uid)
        )
      )
        throw new Error('The room changed.')
      if (value.phase === 'ended') {
        this.end('room-ended')
        return
      }
      const floorExpiry = date(value.floor?.expiresAt),
        person = session.scope.members.find(member => member.uid === value.floor?.uid)
      const speaker =
        session.input.mode === 'ptt' &&
        person &&
        floorExpiry > this.clock() &&
        floorExpiry <= this.clock() + 9000
          ? {
              uid: person.uid,
              name: person.uid === session.uid ? 'You' : person.name,
              expiresAt: floorExpiry,
            }
          : null
      this.clearTimer(session.speakerTimer)
      session.timers.delete(session.speakerTimer)
      session.speakerTimer = null
      if (speaker)
        session.speakerTimer = this.timer(
          session,
          () => {
            if (this.current(session) && session.view.speaker?.expiresAt === speaker.expiresAt)
              this.publish(session, { speaker: null })
          },
          speaker.expiresAt - this.clock()
        )
      this.publish(session, {
        phase: session.reconnecting
          ? 'reconnecting'
          : value.phase === 'connected'
            ? 'connected'
            : 'connecting',
        speaker,
        people: value.people.map(person => ({
          uid: person.uid,
          name: text(person.name) || 'Teammate',
          phase: text(person.phase, 30),
        })),
        error: session.reconnecting ? 'Connection interrupted. Your microphone is paused.' : null,
      })
    } catch (_) {
      if (this.current(session)) {
        this.end('status-unavailable')
        return
      }
    } finally {
      session.polling = false
      if (this.current(session))
        session.pollTimer = this.timer(session, () => this.poll(session), 1500)
    }
  }

  async refresh(session) {
    if (!this.current(session) || session.refreshing || !session.grant) return
    session.refreshing = true
    try {
      const value = await this.call('refreshNativeStaffTeamVoiceCredential', {
        ...this.ownInput(session),
        proof: session.grant.proof,
      })
      if (!this.current(session)) {
        if (this.session === session) this.end('access-changed')
        return
      }
      if (
        value?.uid !== session.uid ||
        value.nativeProtocol !== PROTOCOL ||
        value.roomId !== session.grant.roomId ||
        value.claimId !== session.grant.claimId ||
        value.identity !== session.grant.identity ||
        typeof value.token !== 'string' ||
        value.token.length > 16000 ||
        !/^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(value.token) ||
        !(date(value.expiresAt) > this.clock() && date(value.expiresAt) <= this.clock() + 130000)
      )
        throw new Error('The credential changed.')
      session.device.updateToken(value.token)
    } catch (_) {
      if (this.current(session)) this.end('credential-unavailable')
    } finally {
      session.refreshing = false
    }
  }

  ownInput(session) {
    return {
      actorUid: session.uid,
      nativeProtocol: PROTOCOL,
      roomId: session.grant?.roomId || session.input.roomId,
      acceptanceId: session.input.acceptanceId,
      transport: 'web',
    }
  }

  mute(value) {
    const session = this.session
    if (!session || !this.current(session) || session.reconnecting || session.input.mode !== 'open')
      return
    session.connection?.mute(!!value)
    this.publish(session, { muted: !!value })
  }

  async press() {
    const session = this.session
    if (
      !session ||
      !this.current(session) ||
      session.input.mode !== 'ptt' ||
      session.view.phase !== 'connected' ||
      session.floor
    )
      return
    const floor = { id: this.uuid(), held: true }
    session.floor = floor
    this.publish(session, { waitingForFloor: true, error: null })
    try {
      await this.floor(session, floor, 'begin')
    } catch (_) {
      if (this.session === session && session.floor === floor) {
        this.publish(session, {
          error: 'The channel is busy or could not confirm your turn. Release and try again.',
        })
        this.release()
      }
    }
  }

  async floor(session, floor, action) {
    const value = await this.call('nativeStaffTeamVoiceFloor', {
      ...this.ownInput(session),
      floorId: floor.id,
      action,
    })
    if (!this.current(session) || session.floor !== floor || !floor.held) {
      this.endFloor(session, floor)
      if (this.session === session && !this.current(session)) this.end('access-changed')
      return
    }
    const expires = date(value?.expiresAt)
    if (
      value?.uid !== session.uid ||
      value.nativeProtocol !== PROTOCOL ||
      value.roomId !== session.grant.roomId ||
      value.floorId !== floor.id ||
      value.phase !== 'active' ||
      !(expires > this.clock() && expires <= this.clock() + 9000)
    )
      throw new Error('The speaker lease changed.')
    floor.expires = expires
    session.connection?.mute(false)
    this.publish(session, { transmitting: true, waitingForFloor: false, error: null })
    this.timer(
      session,
      () => {
        if (session.floor === floor && this.clock() >= floor.expires) this.release()
      },
      expires - this.clock()
    )
    this.timer(
      session,
      () => {
        if (this.current(session) && session.floor === floor && floor.held)
          this.floor(session, floor, 'renew').catch(() => {
            if (this.session === session && session.floor === floor) this.release()
          })
      },
      3000
    )
  }

  release() {
    const session = this.session
    if (!session || session.input.mode !== 'ptt') return
    session.connection?.mute(true)
    const floor = session.floor
    session.floor = null
    this.publish(session, { transmitting: false, waitingForFloor: false })
    if (floor) {
      floor.held = false
      this.endFloor(session, floor)
    }
  }

  endFloor(session, floor) {
    if (this.identity()?.uid !== session.uid) return
    this.call('nativeStaffTeamVoiceFloor', {
      ...this.ownInput(session),
      floorId: floor.id,
      action: 'end',
    }).catch(() => {})
  }

  cleanup(session) {
    if (this.identity()?.uid !== session.uid) return Promise.resolve()
    const joining = !!session.input.roomId || !!session.grant
    return this.call(
      joining ? 'leaveNativeStaffTeamVoice' : 'cancelNativeStaffTeamVoice',
      joining ? this.ownInput(session) : session.input
    ).catch(() => {})
  }

  end(reason = 'left') {
    const session = this.session
    if (!session || session.closing) return
    this.release()
    session.closing = true
    session.timers.forEach(timer => this.clearTimer(timer))
    session.timers.clear()
    session.endReason = reason
    this.publish(session, {
      phase: 'closing',
      error: ERROR_REASONS.has(reason) ? ENDED_ERROR : session.view.error,
    })
    // The server update is best-effort (cleanup swallows its failure) and never gates teardown.
    this.cleanup(session)
    // Bound the wait for the SDK's terminal proof. terminal() clears this timer when proof arrives.
    this.timer(session, () => this.forceRelease(session), TEARDOWN_TIMEOUT_MS)
    this.stopMedia(session)
  }

  // The SDK never confirmed it stopped. Stop every media handle this tab holds, then release the
  // session and its audio ownership. Releasing is safe here: the Device is destroyed, so it can open
  // no new signaling stream, and a connect() that still settles later lands in prepare(), which finds
  // this session no longer current and disconnects that call while it is muted, before its media opens.
  forceRelease(session) {
    if (session.terminal) return
    const connection = session.connection
    try {
      connection?.mute(true)
    } catch {
      /* best effort */
    }
    for (const stream of [connection?.getLocalStream?.(), connection?.getRemoteStream?.()]) {
      try {
        stream?.getTracks?.().forEach(track => track.stop())
      } catch {
        /* best effort */
      }
    }
    try {
      connection?.disconnect()
    } catch {
      /* best effort */
    }
    try {
      session.device?.destroy()
    } catch {
      /* best effort */
    }
    console.warn('[TeamVoice] Audio teardown was not confirmed in time; released this tab', {
      reason: session.endReason,
      connectPending: session.connectPending,
      connection: connection?.status?.() || null,
    })
    this.terminal(session)
  }

  stopMedia(session) {
    try {
      session.connection?.mute(true)
      session.connection?.disconnect()
    } catch {
      /* terminal proof still required */
    }
    try {
      session.device?.destroy()
    } catch {
      /* pending SDK cannot be assumed closed */
    }
    if (
      !session.connectPending &&
      (!session.connection || session.connection.status?.() === 'closed')
    )
      this.terminal(session)
  }

  terminal(session) {
    if (session.terminal) return
    session.terminal = true
    session.closing = true
    if (session.floor) {
      session.floor.held = false
      this.endFloor(session, session.floor)
      session.floor = null
    }
    session.timers.forEach(timer => this.clearTimer(timer))
    session.timers.clear()
    this.cleanup(session)
    try {
      session.device?.destroy()
    } catch {
      /* actual terminal event already observed */
    }
    session.grant = null
    session.lease.release()
    if (this.session === session) {
      this.session = null
      this.onChange(null)
    }
  }
}
